import React, { JSX, useContext, useEffect, useRef, useState } from "react"
import { cloneDeep, isEqual, sum } from "lodash";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { printNumberAsString } from "@app/shared/waffles";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard/BenchmarkingContext";
import { QHPFiltersContext } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPFiltersContext";
import QHPNetworkFiltersTable from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPNetworkFiltersTable";
import QHPNetworkPill from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPNetworkPill";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";
import { QHPParentOrganization } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPParentOrganization";
import { QHPParentOrganizationSortingTypeEnum } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPParentOrganizationSortingTypeEnum";

const QHPNetworkFilters = (): JSX.Element => {
  const [defaultPillDisplayCount, setDefaultPillDisplayCount] = useState<number>(null);
  const [showingMore, setShowingMore] = useState<boolean>(false);
  const [sortedParentOrganizations, setSortedParentOrganizations] = useState<QHPParentOrganization[]>([]);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const qhpFiltersContext = useContext(QHPFiltersContext);
  const benchmarkingContext = useContext(BenchmarkingContext);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!!qhpFiltersContext.selectedSortType && !!ref?.current) {
      const parentOrgs = qhpFiltersContext.sortNetworks(cloneDeep(qhpFiltersContext.parentOrganizations)) as QHPParentOrganization[];

      if (!showingMore) {
        // the purpose of these calculations is to display as many pills as possible within two rows
        // while taking into account varying screen sizes and pill sizes
        // and also keeping the "show more" button in line with the pills (otherwise, container height toggling would've worked just fine)
        const getTextWidth = (text: string) => {
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          context.font = getComputedStyle(document.body)?.font;

          return context.measureText(text)?.width;
        }

        // row one's maximum width consists of the available space, minus padding,
        const row1Width = ref.current.clientWidth - 20;
        // row two's maximum width consists of the available space, minus padding,
        // minus the width for the load more button that we want to display in line
        const loadMoreWidth = getTextWidth("Load More") + 48;
        const row2Width = ref.current.clientWidth - 20 - loadMoreWidth;
        let rowOneSize = 0;
        let rowTwoSize = 0;
        let rowOnePillCount = 0;
        let rowTwoPillCount = 0;

        for (let i = 0; i < parentOrgs.length; i++) {
          if (!rowOnePillCount || !rowTwoPillCount) {
            // pill width consists of the larger of the two text widths (name or membership, capped at 196) plus the normal base size of a pill
            const nameWidth = getTextWidth(parentOrgs[i].parentOrganizationName);
            const issuerIdWidth = getTextWidth(`Issuer Id: ${printNumberAsString(sum(parentOrgs[i].hContracts.map(x => x.issuerId)), "---")}`);
            const largestTextWidth = nameWidth > issuerIdWidth ? nameWidth : issuerIdWidth;
            const pillWidth = (largestTextWidth < 196 ? largestTextWidth : 196) + 71;

            if (rowOneSize + pillWidth < row1Width) {
              rowOneSize = rowOneSize + pillWidth;
            } else {
              if (!rowOnePillCount) {
                rowOnePillCount = i;
              }

              if (rowTwoSize + pillWidth < row2Width) {
                rowTwoSize = rowTwoSize + pillWidth;
              } else {
                if (!rowTwoPillCount) {
                  rowTwoPillCount = i - rowOnePillCount;
                }
              }
            }
          }
        }

        if (!!rowOnePillCount && !!rowTwoPillCount) {
          // only assign default pill display count if both rows will be full
          setDefaultPillDisplayCount(rowOnePillCount + rowTwoPillCount);
        }
      }

      setSortedParentOrganizations(parentOrgs);
    }
  }, [qhpFiltersContext.selectedSortType, ref?.current]);

  const onParentOrganizationToggle = (parentOrganization: QHPParentOrganization): void => {
    const newParentOrganizations: QHPParentOrganization[] = cloneDeep(qhpFiltersContext.selectedParentOrganizations);

    if (newParentOrganizations.map(x => x.parentOrganizationName).includes(parentOrganization.parentOrganizationName)) {
      const selectedNetworks: QHPNetwork[] = cloneDeep(benchmarkingFiltersContext.selectedNetworks) as QHPNetwork[];

      // when deselecting a parent organization, also make sure to deselect any h contracts that correspond to that parent organization
      const parentOrganizationContractIds = parentOrganization.hContracts.map(x => x.contractId);
      const selectedContractIds = benchmarkingFiltersContext.selectedNetworks.map(x => x.contractId);

      if (selectedContractIds.some(x => parentOrganizationContractIds.includes(x))) {
        benchmarkingFiltersContext.setSelectedNetworks(selectedNetworks.filter(x => !parentOrganizationContractIds.includes(x.contractId)));
      }

      qhpFiltersContext.setSelectedParentOrganizations(newParentOrganizations.filter(x => x.parentOrganizationName !== parentOrganization.parentOrganizationName));
    } else {
      // ensure that if "your qes networks" is selected, it is always in the first position
      if (parentOrganization.parentOrganizationName === "Your QES Networks") {
        qhpFiltersContext.setSelectedParentOrganizations([parentOrganization].concat(newParentOrganizations));
      } else {
        qhpFiltersContext.setSelectedParentOrganizations(newParentOrganizations.concat(parentOrganization));
      }
    }
  }

  const clearDisabled = (): boolean => {
    if (!qhpFiltersContext.networkFiltersLoading && (
      !!qhpFiltersContext.selectedParentOrganizations.length || !!benchmarkingFiltersContext.selectedNetworks.length
    )) {
      return false;
    }

    return true;
  }

  const submitDisabled = (): boolean => {
    const networksEqual = isEqual(
      cloneDeep(benchmarkingFiltersContext.displayedNetworkFilters).sort((a, b) => a.contractId.localeCompare(b.contractId)),
      cloneDeep(benchmarkingFiltersContext.selectedNetworks).sort((a, b) => a.contractId.localeCompare(b.contractId))
    );

    if (!qhpFiltersContext.networkFiltersLoading &&
      benchmarkingFiltersContext.selectedNetworks.length > 1 &&
      benchmarkingFiltersContext.selectedNetworks.length < 6 &&
      !networksEqual
    ) {
      return false;
    }

    return true;
  }

  return (
    <React.Fragment>
      {!qhpFiltersContext.networkFiltersLoading ? (
        <React.Fragment>
          <div id="benchmarking-dashboard__network-filters--container">
            <hr className="w-100" />
            <div className="row w-100">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-end">
                  <div>
                    <label className="label-m mb0 d-block">
                      Comparison Networks
                    </label>

                    <label className={`copy-text-default d-block mb-3 ${!benchmarkingFiltersContext.displayedPrimaryFilters && "hidden-instructions"}`}>
                      Please select at least 2 and up to 5 networks for comparison.
                    </label>
                  </div>

                  <div>
                    <QESSelect
                      containerClassName="mb-3"
                      testId="network-benchmarking-dashboard__network-filters--parent-organization-sort-by"
                      label="Sort By"
                      onChange={(e) => qhpFiltersContext.setSelectedSortType(e.target.value as QHPParentOrganizationSortingTypeEnum)}
                      value={qhpFiltersContext.selectedSortType || ""}
                    >
                      {Object.values(QHPParentOrganizationSortingTypeEnum).map((sortingType, index) => (
                        <option
                          key={index}
                          value={sortingType}
                        >
                          {sortingType}
                        </option>
                      ))}
                    </QESSelect>
                  </div>
                </div>
              </div>
            </div>

            <div className="row w-100">
              <div className="col-12 d-flex flex-wrap align-items-end" ref={ref}>
                {((!showingMore && !!defaultPillDisplayCount) ? sortedParentOrganizations.slice(0, defaultPillDisplayCount) : sortedParentOrganizations).map((parentOrganization, index) => (
                  <QHPNetworkPill
                    key={index}
                    onParentOrganizationClick={onParentOrganizationToggle}
                    parentOrganization={parentOrganization}
                    selected={qhpFiltersContext.selectedParentOrganizations.map(x => x.parentOrganizationName).includes(parentOrganization.parentOrganizationName)}
                  />
                ))}

                {(!!defaultPillDisplayCount && sortedParentOrganizations.length > defaultPillDisplayCount) && (
                  <QESButton
                    qesTestId="network-benchmarking-dashboard__network-filters--show-more-less"
                    qesType="secondary"
                    onClick={() => setShowingMore(!showingMore)}
                    className="me-3"
                  >
                    Show {showingMore ? "Less" : "More"}
                  </QESButton>
                )}
              </div>
            </div>

            <div className="row w-100 mt-3" id="benchmarking-dashboard__network-filters--networks">
              {!!qhpFiltersContext.selectedParentOrganizations.length && (
                qhpFiltersContext.selectedParentOrganizations.map((parentOrganization, index) => (
                  <div className="col-md-4 benchmarking-network" key={index}>
                    <label className="label-s mb-0 mt-1">
                      {parentOrganization.parentOrganizationName}
                    </label>

                    <QHPNetworkFiltersTable
                      data={parentOrganization.hContracts}
                      parentOrganizationName={parentOrganization.parentOrganizationName}
                      selectedContractIds={benchmarkingFiltersContext.selectedNetworks.map(x => x.contractId)}
                    />
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-end mt-3">
                <QESButton
                  disabled={clearDisabled()}
                  qesTestId="network-benchmarking-dashboard__network-filters--clear"
                  qesType="secondary-danger"
                  onClick={() => qhpFiltersContext.onClearNetworkFiltersClick()}
                  className="me-3"
                >
                  Clear
                </QESButton>

                <QESButton
                  disabled={submitDisabled()}
                  qesTestId="network-benchmarking-dashboard__network-filters--submit"
                  qesType="primary"
                  onClick={() => benchmarkingContext.onSubmitNetworkFiltersClick(qhpFiltersContext.parentOrganizations)}
                >
                  Submit
                </QESButton>
              </div>
            </div>
          </div>
        </React.Fragment>
      ) : (
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-center mt-3">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default QHPNetworkFilters;
